import { axiosBaseQuery } from "@picovico/helpers";
import { createApi } from "@reduxjs/toolkit/query/react";
import {
  MenuCategoryListSuccessResponse,
  MenuComboPackageUnpaginatedListSuccessResponse,
  MenuItemsListSuccessResponse,
  WhatsAppCheckoutFields,
} from "custom-types";
import {
  FoodProcessDTO,
  FoodProcessResponse,
  OrderDetailResponse,
  PaymentVerifyResponse,
  QueryParams,
} from "types/api";

export const foodBookingApi = createApi({
  baseQuery: axiosBaseQuery(),
  tagTypes: ["foodBooking"],
  reducerPath: "foodBookingApi",
  endpoints: (builder) => ({
    getCategory: builder.query<MenuCategoryListSuccessResponse, QueryParams>({
      query: ({ cinema_hall, ...queryData }) => ({
        name: "category",
        method: "GET",
        params: {
          paginate: false,
          ...queryData,
        },
        headers: {
          cinemaHall: cinema_hall,
        },
      }),
    }),
    // Unpaginated: the food page filters its category tabs client side, so it needs every item.
    getMenuItems: builder.query<MenuItemsListSuccessResponse, QueryParams>({
      query: ({ cinema_hall, ...queryData }) => ({
        name: "menu-items",
        method: "GET",
        params: {
          paginate: false,
          ...queryData,
          // After the spread, not before: the admin and the storefront share
          // this endpoint, and a caller that forgot the filter would leak items
          // the cinema deliberately hid.
          show_on_web: true,
        },
        headers: {
          cinemaHall: cinema_hall,
        },
      }),
    }),
    getComboPackages: builder.query<
      MenuComboPackageUnpaginatedListSuccessResponse,
      QueryParams
    >({
      query: ({ cinema_hall, ...queryData }) => ({
        name: "combo-packages",
        method: "GET",
        params: {
          paginate: false,
          ...queryData,
          // See `getMenuItems`.
          show_on_web: true,
        },
        headers: {
          cinemaHall: cinema_hall,
        },
      }),
    }),
    getBookingOrderDetail: builder.query<OrderDetailResponse, QueryParams>({
      query: ({ cinema_hall, ...queryData }) => ({
        name: "getBookingDetail",
        method: "GET",
        params: {
          ...queryData,
        },
        headers: {
          cinemaHall: cinema_hall,
        },
      }),
    }),
    paymentProceed: builder.mutation<
      FoodProcessResponse,
      FoodProcessDTO & {
        cinema_hall: string;
        /** Discount code, applied by dolby as the order is created and netted into its total. */
        voucher_code?: string;
      }
    >({
      query: ({ cinema_hall, ...data }) => ({
        name: "foodBookingProceed",
        method: "POST",
        data,
        headers: {
          cinemaHall: cinema_hall,
        },
      }),
    }),
    foodCheckout: builder.mutation<
      any,
      {
        food_order: string;
        payment_method: string;
        ticket_code: string;
        cinema_hall?: string;
      } & WhatsAppCheckoutFields
    >({
      query: ({ cinema_hall, ...data }) => ({
        name: "food_checkout",
        method: "POST",
        data,
        headers: {
          cinemaHall: cinema_hall,
        },
      }),
    }),
    foodPaymentVerify: builder.mutation<PaymentVerifyResponse, QueryParams>({
      query: ({ pidx, cinema_hall }) => ({
        name: "food_payment_verify",
        method: "POST",
        data: {
          pidx,
        },
        headers: {
          cinemaHall: cinema_hall,
        },
      }),
    }),
    generateFoodTicket: builder.mutation<Blob, QueryParams>({
      query: ({ idx, cinema_hall }) => ({
        responseType: "blob",
        name: "generate_food_ticket",
        method: "GET",
        idx,
        headers: {
          cinemaHall: cinema_hall,
        },
      }),
    }),
  }),
});

export const {
  useGetCategoryQuery,
  useGetMenuItemsQuery,
  useLazyGetMenuItemsQuery,
  useGetComboPackagesQuery,
  useLazyGetComboPackagesQuery,
  useGetBookingOrderDetailQuery,
  useLazyGetBookingOrderDetailQuery,
  usePaymentProceedMutation,
  useFoodCheckoutMutation,
  useFoodPaymentVerifyMutation,
  useGenerateFoodTicketMutation,
} = foodBookingApi;
